import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@teamsparta/stack-button';
import { Flex } from '@teamsparta/stack-flex';
import type { KeyboardEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { usePostCommunityPostSubComment } from '@/hooks/react-query/community/subComment';
import { sendLog } from '@/lib/utils/send-log';
import { TextArea } from '@/pages/community/shared';

import type { CommentSchema } from '../../schema';
import { commentSchema } from '../../schema';

interface Props {
  postId: string;
  commentId: string;
  defaultContent?: string;
  onCancel: () => void;
  onSubmit: () => void;
}

export function CreateSubCommentForm({
  postId,
  commentId,
  defaultContent,
  onCancel,
  onSubmit,
}: Props) {
  const form = useForm<CommentSchema>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      content: defaultContent ?? '',
    },
  });

  const { mutateAsync: postSubComment, isPending } =
    usePostCommunityPostSubComment({ postId, commentId });

  async function handleSubmit({ content }: CommentSchema) {
    await postSubComment(
      { commentId, content },
      {
        onSuccess: () => {
          form.reset();
          onSubmit();
          sendLog('hh_community_post_comment_submit', {
            post_id: postId,
            type: '대댓글',
          });
        },
      },
    );
  }

  function handleCancel() {
    form.reset();
  }

  function handleKeyPress(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (isPending) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();

      form.handleSubmit(handleSubmit)();
    }
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} style={{ width: '100%' }}>
      <Controller
        control={form.control}
        name='content'
        render={({ field }) => (
          <Flex.Column gap={8} fullWidth align='end'>
            <TextArea.Root
              {...field}
              placeholder='댓글을 남겨보세요'
              autoFocus
              onKeyPress={handleKeyPress}
            />
            <Flex.Row gap={8}>
              <Button
                size='sm'
                type='button'
                variant='solid'
                colorScheme='tertiary'
                onClick={() => {
                  handleCancel();
                  onCancel();
                }}
              >
                취소
              </Button>
              <Button
                size='sm'
                type='submit'
                disabled={!form.formState.isValid || isPending}
              >
                등록
              </Button>
            </Flex.Row>
          </Flex.Column>
        )}
      />
    </form>
  );
}
